// "Gösterge istisnaları (n)" katlanır bölümü — portal yetki v2 §8.1/§4.1. SearchSelect ile
// gösterge ara (200+ seçenekte yerel <select> yok; seçenek etiketi "etiket — kategori yolu") →
// satır eklenir; satırda aynı üç durumlu radio grubu + kaldır. İstisna kategori seviyesinin YERİNE
// geçer (genişletebilir de daraltabilir de); kategori seviyesiyle aynı sonucu veriyorsa "etkisiz"
// gri, sonradan çocuk kazanan düğümde ve kapalı girdiden hesaplanan açık göstergelerde uyarı.

import { useMemo } from "react";
import type { ClientAdminSet, KpiEffectiveClientAccess } from "@mahir/shared";
import { CLIENT_ADMIN_COPY } from "../../lib/client-admin-copy";
import {
  becameCategoryWarning,
  derivedOpenDependents,
  overrideCandidates,
  overrideIsNoop,
  removeOverride,
  setOverride,
  type SetDraftV2,
} from "../../lib/client-access-draft-v2";
import { SearchSelect } from "../common/SearchSelect";
import { AccessCategoryRadios } from "./AccessCategoryRadios";

const C = CLIENT_ADMIN_COPY.drawer;

interface Props {
  set: ClientAdminSet;
  draft: SetDraftV2;
  disabled: boolean;
  onChange: (next: SetDraftV2) => void;
}

export function AccessOverridesSection({ set, draft, disabled, onChange }: Props): JSX.Element {
  const auditor = draft.role === "auditor";
  const candidates = useMemo(() => overrideCandidates(set, draft), [set, draft]);
  const options = useMemo(
    () =>
      candidates.map((c) => ({
        value: c.id,
        label: C.overrides.optionLabel(c.label, c.path),
      })),
    [candidates],
  );
  const rows = [...draft.overrides.keys()]
    .map((id) => set.indicators.find((i) => i.id === id))
    .filter((i): i is NonNullable<typeof i> => i !== undefined);

  function add(indicatorId: string): void {
    if (!indicatorId) return;
    const ind = set.indicators.find((i) => i.id === indicatorId);
    // Varsayılan: açık; danışman/rol tavanı yalnız görüntülemeyse görüntüle.
    const level: KpiEffectiveClientAccess =
      auditor || ind?.consultantAccess === "readonly" ? "readonly" : "open";
    onChange(setOverride(draft, indicatorId, level));
  }

  return (
    <details data-testid="access-overrides" className="rounded-lg" style={{ minWidth: 0 }}>
      <summary
        className="text-sm cursor-pointer"
        style={{ color: "var(--color-text-2)", fontWeight: 600 }}
      >
        {C.overrides.title(draft.overrides.size)}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.overrides.help}
        </p>
        {options.length > 0 ? (
          <SearchSelect
            ariaLabel={C.overrides.search}
            value=""
            options={options}
            onChange={add}
            placeholder={C.overrides.search}
            searchPlaceholder={C.overrides.search}
            disabled={disabled}
            size="sm"
            showCode={false}
          />
        ) : (
          <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {C.overrides.noCandidates}
          </p>
        )}
        {rows.length === 0 ? (
          <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
            {C.overrides.empty}
          </p>
        ) : (
          <ul className="flex flex-col gap-1" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {rows.map((ind) => {
              const level = draft.overrides.get(ind.id) ?? "open";
              const ceilingReadonly = ind.consultantAccess !== "open";
              const warnLabels = derivedOpenDependents(set, draft, ind.id);
              const noop = overrideIsNoop(draft, set, ind.id);
              return (
                <li
                  key={ind.id}
                  data-testid="access-override-row"
                  className="rounded-md px-2 py-1"
                  style={{ border: "1px solid var(--color-border)" }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <AccessCategoryRadios
                      name={`ovr-${set.id}-${ind.id}`}
                      legend={ind.label}
                      value={level}
                      disabled={disabled}
                      muted={noop}
                      disabledLevels={
                        ceilingReadonly
                          ? { open: CLIENT_ADMIN_COPY.drawer.categories.consultantReadonly }
                          : auditor
                            ? { open: CLIENT_ADMIN_COPY.drawer.categories.auditorReadonly }
                            : undefined
                      }
                      onChange={(lv) => onChange(setOverride(draft, ind.id, lv))}
                    />
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onChange(removeOverride(draft, ind.id))}
                      className="text-xs shrink-0 disabled:opacity-50"
                      style={{ color: "var(--color-error)", fontWeight: 600, minHeight: 32 }}
                      aria-label={`${C.overrides.remove}: ${ind.label}`}
                    >
                      {C.overrides.remove}
                    </button>
                  </div>
                  {noop ? (
                    <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
                      {C.overrides.noop}
                    </p>
                  ) : null}
                  {becameCategoryWarning(set, ind.id) ? (
                    <p
                      role="note"
                      className="text-xs rounded px-2 py-1 mt-1"
                      style={{
                        background: "var(--color-warning-bg)",
                        color: "var(--color-warning-text)",
                        border: "1px solid var(--color-warning-border)",
                      }}
                    >
                      {C.overrides.becameCategory}
                    </p>
                  ) : null}
                  {warnLabels.length > 0 ? (
                    <p
                      role="note"
                      className="text-xs rounded px-2 py-1 mt-1"
                      style={{
                        background: "var(--color-warning-bg)",
                        color: "var(--color-warning-text)",
                        border: "1px solid var(--color-warning-border)",
                      }}
                    >
                      {C.overrides.derivedWarn(warnLabels)}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </details>
  );
}
